import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
};

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact) {
      toast.error("Please fill in all fields.");
      return;
    }
    const digits = formData.contact.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success("We'll be in touch.");
      setFormData({ name: "", contact: "" });
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-6 md:px-16 lg:px-24 py-12 md:py-16"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "rgb(var(--panel))",
            color: "rgb(var(--ink))",
            border: "1px solid rgb(var(--sig))",
            borderRadius: 0,
            fontFamily: "'IBM Plex Sans', sans-serif",
          },
        }}
      />

      <div className="section-divider mb-10 md:mb-14" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8">
        {/* Left side */}
        <motion.div {...fadeUp} className="md:col-span-5">
          <div className="brass-line mb-4" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-sig font-['IBM_Plex_Sans'] block mb-3">
            Get Started
          </span>
          <h2
            data-testid="contact-headline"
            className="font-['Oswald'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-ink mb-4"
          >
            Ready to <span className="text-sig">Train?</span>
          </h2>
          <p className="text-sm md:text-base leading-relaxed text-mute font-['IBM_Plex_Sans'] font-light max-w-md mb-4">
            Currently available in Delhi and Gurgaon. Reach out to get started.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-2 h-2 bg-sig animate-pulse" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-sig font-['IBM_Plex_Sans']">
              Now accepting inquiries
            </span>
          </div>
          <a
            href="https://wa.me/919958915302"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-link"
            className="inline-flex items-center gap-3 text-2xl md:text-3xl font-['Oswald'] font-bold text-ink hover:text-sig transition-colors"
          >
            +91 99589 15302
          </a>
          <p className="mt-1 text-[10px] tracking-[0.15em] uppercase text-mute font-['IBM_Plex_Sans']">
            Call or WhatsApp
          </p>
        </motion.div>

        {/* Right side - form */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="md:col-span-6 md:col-start-7"
        >
          {submitted ? (
            <div data-testid="form-success" className="border border-sig/20 p-6 md:p-8">
              <div className="brass-line mb-4" />
              <h3 className="font-['Oswald'] text-2xl font-bold uppercase tracking-tight text-ink mb-2">
                Received.
              </h3>
              <p className="text-sm text-mute font-['IBM_Plex_Sans']">
                We'll be in touch shortly.
              </p>
              <button
                data-testid="submit-another-btn"
                onClick={() => setSubmitted(false)}
                className="mt-4 text-xs tracking-[0.2em] uppercase text-sig hover:text-ink transition-colors font-['IBM_Plex_Sans'] border-b border-sig/30 pb-1"
              >
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div>
                <label className="text-[9px] tracking-[0.3em] uppercase text-sig/70 font-['IBM_Plex_Sans'] block mb-2">
                  Name
                </label>
                <input
                  data-testid="input-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="w-full bg-transparent border-0 border-b border-line px-0 py-3 text-ink placeholder:text-line focus:border-sig focus:outline-none transition-colors text-sm font-['IBM_Plex_Sans']"
                />
              </div>

              <div>
                <label className="text-[9px] tracking-[0.3em] uppercase text-sig/70 font-['IBM_Plex_Sans'] block mb-2">
                  Phone Number
                </label>
                <input
                  data-testid="input-contact"
                  type="tel"
                  required
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  placeholder="Your phone number"
                  className="w-full bg-transparent border-0 border-b border-line px-0 py-3 text-ink placeholder:text-line focus:border-sig focus:outline-none transition-colors text-sm font-['IBM_Plex_Sans']"
                />
              </div>

              <button
                data-testid="submit-form-btn"
                type="submit"
                disabled={submitting}
                className="bg-sig text-sigfg px-10 py-3.5 text-xs font-bold uppercase tracking-[0.25em] font-['IBM_Plex_Sans'] hover:bg-sig2 transition-colors duration-300 disabled:opacity-50"
              >
                {submitting ? "Sending..." : "Get in Touch"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
